import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Swords, Trophy, Clock, CheckCircle, XCircle, X, Dumbbell,
} from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, BUTTON, SECTION_LABEL } from '@/constants/designTokens';
import type { Exercise, Profile } from '@/types/database';

/** Format time remaining for active duels, or status label for others. */
function formatDuelTime(duel: {
  status: string;
  timeframe_hours: number;
  end_at?: string | null;
}): string {
  if (duel.status === 'active' && duel.end_at) {
    const msLeft = new Date(duel.end_at).getTime() - Date.now();
    if (msLeft <= 0) return 'Expired';
    const hours = Math.floor(msLeft / (1000 * 60 * 60));
    const mins = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  }
  if (duel.status === 'completed') return 'Completed';
  if (duel.status === 'expired') return 'Expired';
  if (duel.status === 'declined') return 'Declined';
  if (duel.status === 'cancelled') return 'Cancelled';
  return `${duel.timeframe_hours}h`;
}

export interface Duel {
  id: string;
  challenger_id: string;
  challenged_id: string;
  duel_type: 'pr' | 'volume' | '1rm' | 'consistency' | 'strength';
  exercise_id: string | null;
  exercise_name: string | null;
  category: string | null;
  timeframe_hours: number;
  status: 'pending' | 'active' | 'completed' | 'cancelled' | 'declined' | 'expired';
  winner_user_id: string | null;
  start_at: string | null;
  end_at: string | null;
  created_at: string;
  challenger?: Profile | null;
  challenged?: Profile | null;
  exercises?: Exercise | null;
  // Pro breakdown fields (populated for completed duels)
  challenger_score?: number | null;
  challenged_score?: number | null;
  lift_type?: string | null;
  opponent_id?: string | null;
}

interface DuelCardProps {
  duel: Duel;
  profileById: Record<string, { display_name?: string | null; handle?: string | null }>;
  onAccept: (duelId: string) => void;
  onDecline: (duelId: string) => void;
}

const DUEL_COLORS = {
  CHALLENGER_BLUE: '#3B82F6',
  CHALLENGED_RED: '#EF4444',
} as const;

export default function DuelCard({ duel, profileById, onAccept, onDecline }: DuelCardProps) {
  const { profile } = useAuth();
  const { theme } = useTheme();

  const isChallenger = duel.challenger_id === profile?.id;
  const otherId = isChallenger ? duel.challenged_id : duel.challenger_id;
  const otherProfile = otherId ? profileById[otherId] ?? null : null;
  const otherName = otherProfile?.display_name ?? otherProfile?.handle ?? 'Unknown lifter';
  const timeRemaining = duel.end_at
    ? Math.max(0, new Date(duel.end_at).getTime() - Date.now())
    : 0;
  const hoursLeft = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesLeft = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
  const timeLabel = hoursLeft > 0 ? `${hoursLeft}h ${minutesLeft}m left` : `${minutesLeft}m left`;

  return (
    <View
      style={[
        styles.duelCard,
        { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
      ]}>
      {/* Header: type badge + time */}
      <View style={styles.duelHeader}>
        <View style={[styles.duelTypeBadge, { borderColor: theme.colors.accent }]}>
          <Text style={[styles.duelTypeText, { color: theme.colors.accent }]}>
            {duel.duel_type === 'pr' ? 'PR DUEL' : duel.duel_type === '1rm' ? '1RM CHALLENGE' : duel.duel_type === 'consistency' ? 'CONSISTENCY' : 'VOLUME WAR'}
          </Text>
        </View>
        {duel.status === 'active' && (
          <View style={styles.timeRemaining}>
            <Clock size={14} color={theme.colors.warning} />
            <Text style={[styles.timeText, { color: theme.colors.warning }]}>
              {timeLabel}
            </Text>
          </View>
        )}
      </View>

      {/* Versus display */}
      <View style={styles.duelVersus}>
        <View style={styles.duelParticipant}>
          <View
            style={[
              styles.participantAvatar,
              { backgroundColor: DUEL_COLORS.CHALLENGER_BLUE },
            ]}>
            <Text style={[styles.participantAvatarText, { color: '#FFFFFF' }]}>
              {(isChallenger ? (profile?.display_name ?? profile?.handle ?? 'U') : otherName).charAt(0).toUpperCase()}
            </Text>
          </View>
          <Text style={[styles.participantName, { color: theme.colors.text }]}>
            {isChallenger ? 'You' : otherName}
          </Text>
        </View>

        <Text style={[styles.vsIndicator, { color: theme.colors.text }]}>VS</Text>

        <View style={styles.duelParticipant}>
          <View
            style={[
              styles.participantAvatar,
              { backgroundColor: DUEL_COLORS.CHALLENGED_RED },
            ]}>
            <Text style={[styles.participantAvatarText, { color: '#FFFFFF' }]}>
              {(!isChallenger ? (profile?.display_name ?? profile?.handle ?? 'U') : otherName).charAt(0).toUpperCase()}
            </Text>
          </View>
          <Text style={[styles.participantName, { color: theme.colors.text }]}>
            {!isChallenger ? 'You' : otherName}
          </Text>
        </View>
      </View>

      {/* Exercise info */}
      <View style={[styles.exerciseInfo, { backgroundColor: theme.colors.surfaceVariant }]}>
        <Dumbbell size={16} color={theme.colors.textSecondary} />
        <Text style={[styles.exerciseName, { color: theme.colors.text }]}>
          {duel.exercise_name || duel.lift_type || duel.exercises?.name || duel.category || 'Exercise'}
        </Text>
        <Text style={[styles.duelDuration, { color: theme.colors.textSecondary }]}>
          {formatDuelTime(duel)}
        </Text>
      </View>

      {/* Accept/Decline for incoming pending */}
      {duel.status === 'pending' && duel.challenged_id === profile?.id && (
        <View style={styles.duelActions}>
          <TouchableOpacity
            style={[styles.acceptButton, { backgroundColor: theme.colors.success }]}
            onPress={() => onAccept(duel.id)}>
            <CheckCircle size={18} color={theme.colors.text} />
            <Text style={[styles.actionButtonText, { color: theme.colors.text }]}>
              Accept
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.rejectButton, { borderColor: theme.colors.error }]}
            onPress={() => onDecline(duel.id)}>
            <XCircle size={18} color={theme.colors.error} />
            <Text style={[styles.rejectButtonText, { color: theme.colors.error }]}>
              Decline
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Pro: Detailed breakdown for completed duels */}
      {duel.status === 'completed' && duel.challenger_score != null && duel.challenged_score != null && (
        <View style={[styles.breakdownSection, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Text style={[styles.breakdownTitle, { color: theme.colors.textSecondary }]}>Breakdown</Text>
          <View style={styles.breakdownRow}>
            <Text style={[styles.breakdownName, { color: theme.colors.text }]}>
              {isChallenger ? 'You' : otherName}
            </Text>
            <View style={styles.breakdownBarWrap}>
              <View style={[
                styles.breakdownBar,
                {
                  backgroundColor: duel.winner_user_id === duel.challenger_id ? theme.colors.accent : theme.colors.border,
                  flex: Math.max(duel.challenger_score, 1),
                },
              ]} />
            </View>
            <Text style={[styles.breakdownScore, { color: theme.colors.text }]}>
              {Math.round(duel.challenger_score)}
            </Text>
          </View>
          <View style={styles.breakdownRow}>
            <Text style={[styles.breakdownName, { color: theme.colors.text }]}>
              {!isChallenger ? 'You' : otherName}
            </Text>
            <View style={styles.breakdownBarWrap}>
              <View style={[
                styles.breakdownBar,
                {
                  backgroundColor: duel.winner_user_id === duel.challenged_id ? theme.colors.accent : theme.colors.border,
                  flex: Math.max(duel.challenged_score, 1),
                },
              ]} />
            </View>
            <Text style={[styles.breakdownScore, { color: theme.colors.text }]}>
              {Math.round(duel.challenged_score)}
            </Text>
          </View>
        </View>
      )}

      {/* Outcome banners */}
      {duel.status === 'completed' && duel.winner_user_id && (
        <View
          style={[
            styles.winnerBanner,
            { backgroundColor: duel.winner_user_id === profile?.id ? theme.colors.success : theme.colors.error },
          ]}>
          <Trophy size={16} color={theme.colors.text} />
          <Text style={[styles.winnerText, { color: theme.colors.text }]}>
            {duel.winner_user_id === profile?.id ? 'You Won!' : `${otherName} Won`}
          </Text>
        </View>
      )}

      {duel.status === 'completed' && !duel.winner_user_id && (
        <View style={[styles.winnerBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Text style={[styles.winnerText, { color: theme.colors.textSecondary }]}>Draw — No Winner</Text>
        </View>
      )}

      {duel.status === 'expired' && (
        <View style={[styles.winnerBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Clock size={16} color={theme.colors.textSecondary} />
          <Text style={[styles.winnerText, { color: theme.colors.textSecondary }]}>Expired</Text>
        </View>
      )}

      {duel.status === 'declined' && (
        <View style={[styles.winnerBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
          <XCircle size={16} color={theme.colors.textSecondary} />
          <Text style={[styles.winnerText, { color: theme.colors.textSecondary }]}>Declined</Text>
        </View>
      )}

      {duel.status === 'cancelled' && (
        <View style={[styles.winnerBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
          <X size={16} color={theme.colors.textSecondary} />
          <Text style={[styles.winnerText, { color: theme.colors.textSecondary }]}>Cancelled</Text>
        </View>
      )}

      {duel.status === 'pending' && duel.challenger_id === profile?.id && (
        <View style={[styles.winnerBanner, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Clock size={16} color={theme.colors.accent} />
          <Text style={[styles.winnerText, { color: theme.colors.accent }]}>Awaiting Response</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  duelCard: {
    ...CARD.standard,
    marginBottom: 20,
  },
  duelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  duelTypeBadge: {
    borderWidth: 1,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: RADIUS.none,
  },
  duelTypeText: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: TRACKING.wide,
  },
  timeRemaining: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  timeText: {
    fontSize: FONT.sm,
    fontFamily: TYPO.body.semibold,
  },
  duelVersus: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    marginBottom: 20,
  },
  duelParticipant: {
    alignItems: 'center',
    gap: 8,
  },
  participantAvatar: {
    width: 56,
    height: 56,
    borderRadius: RADIUS.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  participantAvatarText: {
    fontSize: FONT['2xl'],
    fontFamily: TYPO.heading.bold,
  },
  participantName: {
    fontSize: FONT.base,
    fontFamily: TYPO.body.bold,
  },
  vsIndicator: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
  },
  exerciseInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: RADIUS.xs,
    marginBottom: 16,
  },
  exerciseName: {
    flex: 1,
    fontSize: FONT.base,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
  },
  duelDuration: {
    fontSize: FONT.sm,
    fontFamily: TYPO.body.semibold,
  },
  duelActions: {
    flexDirection: 'row',
    gap: 16,
  },
  acceptButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    borderRadius: RADIUS.none,
  },
  rejectButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    borderRadius: RADIUS.none,
    borderWidth: 1,
  },
  actionButtonText: {
    fontSize: FONT.base,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
  },
  rejectButtonText: {
    fontSize: FONT.base,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
  },
  winnerBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: RADIUS.xs,
  },
  winnerText: {
    fontSize: FONT.base,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
  },
  breakdownSection: {
    padding: 16,
    borderRadius: RADIUS.xs,
    marginBottom: 16,
    gap: 12,
  },
  breakdownTitle: {
    ...SECTION_LABEL,
    fontFamily: TYPO.subheading.bold,
    marginBottom: 4,
  },
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  breakdownName: {
    fontSize: FONT.sm,
    fontFamily: TYPO.body.semibold,
    width: 60,
  },
  breakdownBarWrap: {
    flex: 1,
    flexDirection: 'row',
    height: 8,
    borderRadius: RADIUS.sm,
    overflow: 'hidden' as const,
  },
  breakdownBar: {
    height: 8,
    borderRadius: RADIUS.sm,
    minWidth: 4,
  },
  breakdownScore: {
    fontSize: FONT.sm,
    fontFamily: TYPO.heading.bold,
    width: 50,
    textAlign: 'right' as const,
  },
});
